import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Users, Crown, Target, TrendingUp, Phone, ChevronRight } from 'lucide-react'
import clsx from 'clsx'
import axios from 'axios'
import { buildApiUrl } from '../../../config/api'
import { Card } from '../../../components/ui'
import { formatPhone } from '../../../utils/masks'
import { formatRelativeDate } from '../../../utils/dateUtils'

interface DashboardData {
  leader_name: string
  total_people: number
  voters_count: number
  contacts_count: number
  campaign: {
    id: number
    name: string
    election_year: number
    vote_goal: number
    engaged_count: number
    goal_percent: number
    end_date: string | null
  } | null
  recent_voters: { id: number; name: string; phone: string; created_at: string }[]
}

export default function LeaderDashboardPage() {
  const [data, setData] = useState<DashboardData | null>(null)
  const [loading, setLoading] = useState(true)
  const navigate = useNavigate()

  useEffect(() => {
    axios.get(buildApiUrl('/v1/leader/dashboard'))
      .then(res => setData(res.data.data))
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [])

  if (loading) return (
    <div className="space-y-6 animate-pulse">
      <div className="h-8 w-64 bg-gray-200 rounded" />
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {[1,2,3,4].map(i => <div key={i} className="h-24 bg-gray-100 rounded-2xl" />)}
      </div>
      <div className="h-48 bg-gray-100 rounded-2xl" />
    </div>
  )

  if (!data) return (
    <div className="text-center py-20">
      <Crown className="w-12 h-12 text-gray-300 mx-auto mb-3" />
      <p className="text-gray-500">Sua conta ainda não está vinculada a uma liderança.</p>
      <p className="text-sm text-gray-400 mt-1">Entre em contato com o administrador da organização.</p>
    </div>
  )

  const campaign = data.campaign
  const goalColor = campaign
    ? campaign.goal_percent >= 100 ? 'text-success-600' : campaign.goal_percent >= 50 ? 'text-warning-600' : 'text-error-500'
    : ''
  const goalBg = campaign
    ? campaign.goal_percent >= 100 ? '#22c55e' : campaign.goal_percent >= 50 ? '#f59e0b' : '#ef4444'
    : ''

  function getDaysRemaining(): string | null {
    if (!campaign?.end_date) return null
    const end = new Date(campaign.end_date + 'T23:59:59')
    const diff = Math.ceil((end.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24))
    if (diff < 0) return 'Encerrada'
    if (diff === 0) return 'Hoje'
    return `${diff} dias`
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <p className="text-gray-500 text-sm">Bem-vindo,</p>
        <h1 className="text-2xl font-bold text-gray-900">{data.leader_name}</h1>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {[
          { icon: <Users className="w-5 h-5 text-brand-500" />, value: data.total_people, label: 'Pessoas' },
          { icon: <Crown className="w-5 h-5 text-emerald-500" />, value: data.voters_count, label: 'Eleitores' },
          { icon: <Users className="w-5 h-5 text-blue-500" />, value: data.contacts_count, label: 'Contatos' },
          { icon: <Target className="w-5 h-5 text-amber-500" />, value: campaign?.engaged_count ?? 0, label: 'Na campanha' },
        ].map(s => (
          <div key={s.label} className="bg-white rounded-2xl border border-gray-200 p-4">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-9 h-9 rounded-xl bg-gray-50 flex items-center justify-center">{s.icon}</div>
            </div>
            <p className="text-2xl font-semibold text-gray-900">{s.value.toLocaleString('pt-BR')}</p>
            <p className="text-xs text-gray-500 mt-0.5">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Campanha ativa */}
      {campaign && (
        <Card>
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Campanha ativa</h3>
              <p className="text-lg font-bold text-gray-900 mt-1">{campaign.name}</p>
              <p className="text-xs text-gray-500">{campaign.election_year}{getDaysRemaining() ? ` · Faltam ${getDaysRemaining()}` : ''}</p>
            </div>
            <div className="text-right">
              <p className={clsx('text-3xl font-bold', goalColor)}>{campaign.goal_percent}%</p>
              <p className="text-xs text-gray-500">da meta</p>
            </div>
          </div>

          {/* Barra de progresso */}
          <div className="mb-3">
            <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
              <div className="h-full rounded-full transition-all" style={{ width: `${Math.min(campaign.goal_percent, 100)}%`, backgroundColor: goalBg }} />
            </div>
          </div>

          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-500">
              <strong className="text-gray-800">{campaign.engaged_count.toLocaleString('pt-BR')}</strong> engajados
            </span>
            <span className="text-gray-500">
              Meta: <strong className="text-gray-800">{campaign.vote_goal.toLocaleString('pt-BR')}</strong>
            </span>
          </div>
        </Card>
      )}

      {/* Últimos contatos */}
      <Card padding="none">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h3 className="text-sm font-semibold text-gray-800">Últimos contatos adicionados</h3>
          <button onClick={() => navigate('/leader/voters')} className="text-xs text-brand-500 hover:text-brand-600 font-medium flex items-center gap-1">
            Ver todos <ChevronRight className="w-3 h-3" />
          </button>
        </div>
        {data.recent_voters.length === 0 ? (
          <div className="py-8 text-center">
            <p className="text-sm text-gray-400">Nenhum contato adicionado ainda</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-50">
            {data.recent_voters.map(v => (
              <div key={v.id} className="flex items-center justify-between px-5 py-3 hover:bg-gray-50/50 transition-colors">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-brand-50 flex items-center justify-center flex-shrink-0">
                    <span className="text-xs font-bold text-brand-600">{v.name.charAt(0)}</span>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">{v.name}</p>
                    {v.phone && <p className="text-xs text-gray-400">{formatPhone(v.phone)}</p>}
                  </div>
                </div>
                <span className="text-xs text-gray-400">{formatRelativeDate(v.created_at)}</span>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  )
}
